import { createSlice } from "@reduxjs/toolkit";
import { sortMissionsByDeploymentDate } from "Components/LiveMissionsTable/liveMissionsUtils";

type Missions = {
  missionsPageFleetId: string | null;
  missions: null | Array<Mission>;
  isUpdating: boolean;
  isMissionsUpdatesPaused: null | boolean;
  missionsUpdatesStorage: Array<Mission | never>;
  lastUpdateAt: string | null;
};

const initialState: Missions = {
  missionsPageFleetId: null,
  missions: null,
  isUpdating: false,
  isMissionsUpdatesPaused: false,
  missionsUpdatesStorage: [],
  lastUpdateAt: null,
};

export const Slice = createSlice({
  name: "missionsPage",
  initialState: initialState,
  reducers: {
    resetMissionsPageData: () => initialState,
    setMissionsPageFleetId: (state, action) => {
      state.missionsPageFleetId = action.payload;
      return state;
    },

    // when picked semantic map was changed
    resetMissions: (state) => {
      state.missions = null;

      return state;
    },
    setMissions: (state, action) => {
      const payload = action.payload;
      state.missions = payload;
      state.lastUpdateAt = new Date().toISOString();
      return state;
    },
    reorderMissions: (state, action) => {
      const ids: string[] | null = action.payload;
      if (!state.missions || !ids?.length) return state;

      // Only reorder the subset of missions whose IDs are in the payload,
      // leaving all other missions (active, paused, etc.) untouched.
      const idSet = new Set(ids);

      // Record the global positions currently held by this subset (in current order)
      const positions: number[] = [];
      state.missions.forEach((m, i) => {
        if (idSet.has(m.id)) positions.push(i);
      });

      // Build the reordered subset using the caller-specified order
      const byId: Record<string, Mission> = {};
      state.missions.forEach((m) => {
        if (idSet.has(m.id)) byId[m.id] = m;
      });
      const reordered = ids
        .map((id) => byId[id])
        .filter((m): m is Mission => !!m);

      // Slot the reordered missions back into the same global positions
      const newMissions = [...state.missions] as Mission[];
      positions.forEach((pos, i) => {
        newMissions[pos] = reordered[i];
      });
      state.missions = newMissions;
      return state;
    },
    updateMissions: (state, action) => {
      // The WS payload from FleetMissionQueueConsumer is a single
      // ChannelsMissionStateEntry dict with shape { id, status, vehicle, ..., data: [] }.
      // The .data field on the mission dict is a LIST of state entries (used by
      // some consumers), NOT a nested mission wrapper. The previous `?.data ||`
      // fallback shadowed the real mission with that list and made the reducer
      // bail on every per-mission delta. Detect by presence of `id`.
      const payload = action.payload;
      let receivedMission: any = null;
      if (payload && typeof payload === "object") {
        if ("id" in payload && payload.id) {
          receivedMission = payload;
        } else if (
          payload.data &&
          typeof payload.data === "object" &&
          !Array.isArray(payload.data) &&
          payload.data.id
        ) {
          receivedMission = payload.data;
        }
      }
      if (!receivedMission || !receivedMission.id) {
        return state;
      }

      // Set lastUpdateAt to current datetime (Date object)
      state.lastUpdateAt = new Date().toISOString();

      if (state.isMissionsUpdatesPaused) {
        state.missionsUpdatesStorage.push(receivedMission);
        return state;
      }

      const isNewMission = !state.missions?.find(
        (mission) => mission?.id === receivedMission?.id
      );

      if (isNewMission) {
        state.missions = sortMissionsByDeploymentDate([
          receivedMission,
          ...(state.missions?.length ? state.missions : []),
        ]);
      } else {
        state.missions =
          state.missions?.map((mission) =>
            mission.id === receivedMission?.id
              ? {
                  ...mission,
                  vehicle: receivedMission?.vehicle,
                  status: receivedMission?.status,
                  passedLoops: receivedMission?.passedLoops,
                  passedStops: receivedMission?.passedStops,
                  startedExecutionAt: receivedMission?.startedExecutionAt,
                  completedAt: receivedMission?.completedAt,
                  stops: receivedMission?.stops,
                  isHot: receivedMission?.isHot,
                  priority: receivedMission?.priority,
                  displayAt: receivedMission?.displayAt,
                }
              : mission
          ) ?? [];
      }
      return state;
    },
    // handleDragEnd
    pauseMissionsUpdates: (state) => {
      state.isMissionsUpdatesPaused = true;
      return state;
    },
    // handleDragEnd
    continueMissionsUpdates: (state) => {
      state.isMissionsUpdatesPaused = false;
      return state;
    },
    // after ejecting all missions from the storage
    resetMissionsUpdatesStorage: (state) => {
      state.missionsUpdatesStorage = [];
      return state;
    },
    // optimistically move a mission to a different vehicle section (or unassigned)
    moveMissionToVehicle: (state, action) => {
      const { missionId, newVehicleId } = action.payload as {
        missionId: string;
        newVehicleId: string | null;
      };
      if (!state.missions) return state;
      state.missions = state.missions.map((m) =>
        m.id === missionId ? { ...m, vehicle: newVehicleId } : m
      ) as Mission[];
      return state;
    },
    // mark a mission as hot job and move it to the front of the queue
    setMissionHot: (state, action) => {
      const missionId: string = action.payload;
      if (!state.missions) return state;

      const index = state.missions.findIndex((m) => m.id === missionId);
      if (index === -1) return state;

      const [mission] = state.missions.splice(index, 1);
      mission.isHot = true;
      state.missions.unshift(mission);
      return state;
    },
    // clear hot job status from a mission and drop it below the remaining
    // hot block, matching the backend's post-unpin dispatch order (remaining
    // hot jobs keep precedence; the unpinned one rejoins the top of the FIFOs)
    clearMissionHot: (state, action) => {
      const missionId: string = action.payload;
      if (!state.missions) return state;

      const index = state.missions.findIndex((m) => m.id === missionId);
      if (index === -1) return state;

      const [mission] = state.missions.splice(index, 1);
      mission.isHot = false;

      let insertAt = 0;
      state.missions.forEach((m, i) => {
        if (m.isHot) insertAt = i + 1;
      });
      state.missions.splice(insertAt, 0, mission);
      return state;
    },
    // update name/stops on the QueuedMission after a new version is created (DEV-1258)
    refreshMissionPlanFields: (
      state,
      action: {
        payload: {
          prevMissionId: string;
          name: string;
          stops: Mission["stops"];
        };
      }
    ) => {
      const { prevMissionId, name, stops } = action.payload;
      state.missions =
        state.missions?.map((mission) =>
          (mission as any).mission === prevMissionId
            ? { ...mission, name, stops }
            : mission
        ) ?? state.missions;
      return state;
    },
  },
});

export default Slice;
